import React, {useState, useEffect} from 'react';
import Dropzone from 'react-dropzone';
import ReactAvatarEditor from 'react-avatar-editor';
import {get} from 'lodash/object';

import {fileToImageUrl, dataURItoBlob, returnFileSize} from 'utils/images';
import {alertByError, alertConfirm, alertError} from 'utils/alerts';

import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  CustomInput,
  Button,
} from 'reactstrap';
import {BaseButton} from 'components/Buttons';
import {isEmpty} from 'lodash';
import {IMAGES_FEEDBACK, ONLY_ACCEPTED_IMAGE, validateImage} from 'configs';
import MuiIcon from 'components/Icons/MuiIcon';

function Component({
  isLoading,
  defaultImage,
  //allowZoomOut,
  isSubmitting,
  onSubmit,
  callback,
  saveBtnProps,
  leftBtnProps,
  rightBtnProps,
  fileSizeLimit = 5,
}) {
  const [editor, setEditor] = useState({});
  const [image, setImage] = useState(
    defaultImage ? fileToImageUrl(defaultImage) : ''
  );
  const [position, setPosition] = useState({x: 0.5, y: 0.5});
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [border] = useState(10);
  const [width] = useState(160);
  const [height] = useState(160);
  const [borderRadius] = useState(0);

  const acceptedFormats = ONLY_ACCEPTED_IMAGE.all;

  useEffect(() => {
    setImage(fileToImageUrl(defaultImage) || '');
  }, [defaultImage]);

  async function handleNewImage(e) {
    const target = e.target;
    const image = target.files[0];
    const isValid = image ? validateImage(image.type) : false;

    if (isValid) {
      setImage(image);
    } else {
      const result = Object.keys(ONLY_ACCEPTED_IMAGE).find(
        (key) => ONLY_ACCEPTED_IMAGE[key] === acceptedFormats
      );

      await alertError({
        title: 'Invalid File Type',
        text: IMAGES_FEEDBACK[result],
      });

      target.value = '';
    }
  }

  function handleScale(e) {
    setScale(parseFloat(e.target.value));
  }

  function handleRotateLeft(e) {
    e.preventDefault();
    setRotate(rotate - 90);
  }

  function handleRotateRight(e) {
    e.preventDefault();
    setRotate(rotate + 90);
  }

  function handlePositionChange(position) {
    setPosition(position);
  }

  function handleDrop(acceptedFiles) {
    setImage(acceptedFiles[0]);
  }

  function handleRef(editor) {
    if (editor) setEditor(editor);
  }

  function handleDropRejected() {
    alertByError({message: `File type is not supported!`});
  }

  async function handleSave(e) {
    e.preventDefault();

    if (!image && isEmpty(defaultImage) && !fileToImageUrl(defaultImage)) {
      await alertError({
        text: `Please upload a image to save.`,
      });

      return;
    }

    const fileSize = returnFileSize(image.size);

    if (fileSizeLimit && fileSizeLimit <= fileSize) {
      await alertError({
        text: `Cannot send file size that is greater than ${fileSizeLimit} MB`,
      });
      return;
    }

    // Original file name from image.
    const imageName = get(image, 'name', 'image.png');

    // Get Data URL and convert to PNG.
    const dataURL = editor.getImageScaledToCanvas().toDataURL('image/png');
    const blob = dataURItoBlob(dataURL);

    let formData = new FormData();
    formData.append('image', blob, imageName);

    try {
      const res = await onSubmit(image ? formData : false);
      callback(res);
    } catch (e) {
      await alertByError(e);
    }
  }

  async function handleDelete() {
    const {value} = await alertConfirm({
      text: 'Are you sure you want to delete this photo?',
    });
    if (value) setImage('');
  }

  return (
    <Row>
      <Col sm="12" md="auto">
        <Dropzone
          onDropRejected={handleDropRejected}
          accept={acceptedFormats}
          onDrop={handleDrop}
          multiple={false}
          disableClick
          style={{
            width: `${width + border * 2}px`,
            height: `${height + border * 2}px`,
          }}
        >
          <ReactAvatarEditor
            className="editor-canvas"
            crossOrigin="anonymous"
            ref={handleRef}
            image={image}
            border={border}
            scale={scale}
            width={width}
            height={height}
            position={position}
            onPositionChange={handlePositionChange}
            rotate={rotate}
            borderRadius={width / (100 / borderRadius)}
          />

          {image && (
            <Button
              outline
              color="white"
              onClick={() => handleDelete()}
              className="text-danger d-block mx-auto"
            >
              Delete
            </Button>
          )}
        </Dropzone>
      </Col>
      <Col>
        <fieldset key={isLoading} disabled={isSubmitting}>
          <FormGroup>
            <Input
              id="file"
              type="file"
              name="file"
              accept={acceptedFormats}
              onChange={handleNewImage}
            />
          </FormGroup>
          <FormGroup row className="align-items-center">
            <Label for="zoom" sm={12} md={3}>
              Zoom
            </Label>
            <Col>
              <CustomInput
                id="zoom"
                type="range"
                onChange={handleScale}
                min="0.1"
                max="2"
                step="0.1"
                defaultValue="1"
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={12} md={3}>
              Rotate
            </Label>

            <Col>
              <Row>
                <Col>
                  <BaseButton
                    color="primary"
                    outline
                    block
                    onClick={handleRotateLeft}
                    {...leftBtnProps}
                  >
                    <MuiIcon name="rotate_left" /> Left
                  </BaseButton>
                </Col>
                <Col>
                  <BaseButton
                    color="primary"
                    outline
                    block
                    onClick={handleRotateRight}
                    {...rightBtnProps}
                  >
                    <MuiIcon name="rotate_right" /> Right
                  </BaseButton>
                </Col>
              </Row>
            </Col>
          </FormGroup>

          <BaseButton
            color="primary"
            block
            onClick={handleSave}
            disabled={isSubmitting}
            {...saveBtnProps}
          >
            {isSubmitting ? 'Saving...' : 'Save changes'}
          </BaseButton>
        </fieldset>
      </Col>
    </Row>
  );
}

export default Component;
